import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import BlogCard from "../components/BlogCard";
import PageMeta from "../components/PageMeta";

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch categories once
  useEffect(() => {
    api.get("/blog-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Fetch posts when category changes
  useEffect(() => {
    setLoading(true);
    setPosts([]);
    const params = new URLSearchParams({ per_page: 9 });
    if (activeCategory) params.set("category", activeCategory);

    api
      .get(`/blog?${params}`)
      .then((r) => {
        setPosts(r.data.data);
        setMeta(r.data.meta);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const loadMore = useCallback(() => {
    if (loadingMore || meta.current_page >= meta.last_page) return;
    setLoadingMore(true);
    const params = new URLSearchParams({ per_page: 9, page: meta.current_page + 1 });
    if (activeCategory) params.set("category", activeCategory);

    api
      .get(`/blog?${params}`)
      .then((r) => {
        setPosts((prev) => [...prev, ...r.data.data]);
        setMeta(r.data.meta);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [activeCategory, loadingMore, meta]);

  const setCategory = (slug) => {
    if (slug) setSearchParams({ category: slug });
    else setSearchParams({});
  };

  return (
    <div data-testid="blog-page">
      <PageMeta
        title="Blog"
        description="Industry insights, product guides, and pickleball manufacturing news from Pickleballgearz."
      />

      {/* Hero header */}
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">
              <span className="mr-2 inline-block h-2 w-2 bg-brand-red" />
              Industry Insights
            </p>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["FROM THE", <>FACTORY<span className="text-brand-red">.</span></>]} />
          </h1>
          <FadeIn delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-500">
              Manufacturing tips, product guides, industry news, and behind-the-scenes from the Pickleballgearz factory floor.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Category filters */}
      {categories.length > 0 && (
        <section className="border-b border-black/10 bg-brand-smoke">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 px-5 py-4 lg:px-10">
            <button
              onClick={() => setCategory("")}
              data-testid="blog-cat-all"
              className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                !activeCategory
                  ? "border-brand-ink bg-brand-ink text-white"
                  : "border-black/15 text-neutral-500 hover:border-brand-ink"
              }`}
            >
              All Posts
              <span className="ml-2 text-[10px] opacity-60">({meta.total})</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setCategory(cat.slug)}
                data-testid={`blog-cat-${cat.slug}`}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                  activeCategory === cat.slug
                    ? "border-brand-ink bg-brand-ink text-white"
                    : "border-black/15 text-neutral-500 hover:border-brand-ink"
                }`}
              >
                {cat.name}
                <span className="ml-2 text-[10px] opacity-60">({cat.count})</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Post grid */}
      <section className="mx-auto max-w-[1400px] px-5 py-16 md:py-24 lg:px-10">
        {loading ? (
          <div className="flex items-center justify-center py-32" data-testid="blog-loading">
            <Loader2 size={28} className="animate-spin text-brand-red" />
          </div>
        ) : posts.length === 0 ? (
          <Reveal>
            <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="blog-empty">
              <p className="font-display text-2xl font-medium text-brand-ink">No posts yet</p>
              <p className="mt-3 text-sm text-neutral-500">
                {activeCategory
                  ? "No posts found in this category."
                  : "Check back soon for industry insights from Pickleballgearz."}
              </p>
              {activeCategory && (
                <button onClick={() => setCategory("")} className="btn-outline mt-6">
                  View All Posts
                </button>
              )}
            </div>
          </Reveal>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
              {posts.map((post, i) => (
                <BlogCard key={post.id} post={post} index={i} />
              ))}
            </div>

            {/* Load more */}
            {meta.current_page < meta.last_page && (
              <Reveal className="mt-14 flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-outline disabled:opacity-50"
                  data-testid="blog-load-more"
                >
                  {loadingMore ? (
                    <><Loader2 size={15} className="animate-spin" /> Loading...</>
                  ) : (
                    <>Load More Posts <ArrowUpRight size={16} /></>
                  )}
                </button>
              </Reveal>
            )}
          </>
        )}
      </section>

      {/* CTA Banner */}
      <section className="border-t border-black/10 bg-brand-smoke py-16">
        <Reveal className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div>
              <p className="mono-caption text-neutral-500">Ready to start?</p>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight">
                Request a wholesale quote today.
              </h2>
            </div>
            <Link to="/inquiry" className="btn-primary shrink-0" data-testid="blog-cta-inquiry">
              Send Inquiry <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Blog;
